import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom-v6'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  Empty,
  EmptyHeader,
  EmptyTitle,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
} from '@clouway/ui'
import { ChevronDown, Pencil, Plus, Trash2 } from 'lucide-react'

import { isMigratedRoleId } from '@/api/access'
import { Permission, hasPermission, useSession } from '@/features/auth'
import { roleDisplayName } from '@/features/users'
import { useCatalogProducts, useCatalogRoles, useDeleteRole } from '../hooks'
import type { CatalogRole } from '../types'

export function RolesList() {
  const { t, i18n } = useTranslation('feature')
  const { access } = useSession()
  const canWrite = hasPermission(access, Permission.roles.create)

  const { data: products, isLoading: productsLoading } = useCatalogProducts()
  const [product, setProduct] = useState('')

  // Single product → auto-select it, same as the Access tab.
  useEffect(() => {
    if (!product && products && products.length > 0) {
      setProduct(products[0])
    }
  }, [product, products])

  const { data: roles, isLoading: rolesLoading } = useCatalogRoles(product)

  if (productsLoading) {
    return <Skeleton className="mx-auto h-64 w-full max-w-3xl" />
  }

  if (!products || products.length === 0) {
    return (
      <Empty>
        <EmptyHeader>
          <EmptyTitle>{t('access.product.empty')}</EmptyTitle>
        </EmptyHeader>
      </Empty>
    )
  }

  const presets = (roles ?? []).filter((role) => !role.custom)
  const migrated = (roles ?? []).filter((role) => role.custom && isMigratedRoleId(role.id))
  const custom = (roles ?? []).filter((role) => role.custom && !isMigratedRoleId(role.id))

  return (
    <div className="mx-auto flex w-full max-w-3xl flex-col gap-4">
      <div className="flex items-center justify-between gap-2">
        {products.length > 1 ? (
          <Select value={product} onValueChange={setProduct}>
            <SelectTrigger className="w-64" aria-label={t('access.product.label')}>
              <SelectValue placeholder={t('access.product.label')} />
            </SelectTrigger>
            <SelectContent>
              {products.map((p) => (
                <SelectItem key={p} value={p}>
                  {p}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : (
          <span />
        )}
        {canWrite && (
          <Button asChild size="sm">
            <Link to={`/roles/new?product=${product}`}>
              <Plus className="size-4" />
              {t('role.list.add')}
            </Link>
          </Button>
        )}
      </div>

      {rolesLoading ? (
        <Skeleton className="h-64 w-full" />
      ) : (
        <>
          <Card>
            <CardHeader>
              <CardTitle className="text-sm">{t('role.list.presets')}</CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col gap-2">
              {presets.length === 0 ? (
                <span className="text-muted-foreground text-sm">{t('role.list.emptyPresets')}</span>
              ) : (
                presets.map((role) => <PresetRow key={role.id} product={product} role={role} />)
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-sm">{t('role.list.custom')}</CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col gap-2">
              {custom.length === 0 ? (
                <span className="text-muted-foreground text-sm">{t('role.list.emptyCustom')}</span>
              ) : (
                custom.map((role) => <CustomRow key={role.id} product={product} role={role} canWrite={canWrite} />)
              )}
            </CardContent>
          </Card>

          {migrated.length > 0 && (
            <Collapsible>
              <Card>
                <CollapsibleTrigger asChild>
                  <CardHeader className="cursor-pointer">
                    <div className="flex items-center justify-between gap-2">
                      <CardTitle className="text-muted-foreground text-sm">
                        {t('role.list.migrated')} ({migrated.length})
                      </CardTitle>
                      <ChevronDown className="size-4" />
                    </div>
                  </CardHeader>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <CardContent className="flex flex-col gap-2">
                    <p className="text-muted-foreground text-xs">{t('role.list.migratedCaption')}</p>
                    {migrated.map((role) => (
                      <div key={role.id} className="flex items-center justify-between gap-2 rounded-md border p-2">
                        <div>
                          <div className="text-sm font-medium">{roleDisplayName(role, i18n.language)}</div>
                          <div className="text-muted-foreground text-xs">{role.id}</div>
                        </div>
                        <Badge variant="outline" className="text-muted-foreground">
                          {t('access.badge.migrated')}
                        </Badge>
                      </div>
                    ))}
                  </CardContent>
                </CollapsibleContent>
              </Card>
            </Collapsible>
          )}
        </>
      )}
    </div>
  )
}

function PresetRow({ product, role }: { product: string; role: CatalogRole }) {
  const { t, i18n } = useTranslation('feature')
  return (
    <div className="flex items-center justify-between gap-2 rounded-md border p-2">
      <div>
        <div className="text-sm font-medium">{roleDisplayName(role, i18n.language)}</div>
        <div className="text-muted-foreground text-xs">{role.id}</div>
      </div>
      <div className="flex items-center gap-2">
        <Badge variant="secondary">{t('access.badge.preset')}</Badge>
        <Button asChild size="icon" variant="ghost" aria-label="view">
          <Link to={`/roles/${product}/${role.id}/edit`}>
            <Pencil className="size-4" />
          </Link>
        </Button>
      </div>
    </div>
  )
}

function CustomRow({ product, role, canWrite }: { product: string; role: CatalogRole; canWrite: boolean }) {
  const { t, i18n } = useTranslation('feature')
  const { t: tCommon } = useTranslation('common')
  const { mutate: remove } = useDeleteRole(product)
  const label = roleDisplayName(role, i18n.language)

  return (
    <div className="flex items-center justify-between gap-2 rounded-md border p-2">
      <div>
        <div className="text-sm font-medium">{label}</div>
        <div className="text-muted-foreground text-xs">{role.id}</div>
      </div>
      <div className="flex items-center gap-2">
        <Badge>{t('access.badge.custom')}</Badge>
        {canWrite && (
          <>
            <Button asChild size="icon" variant="ghost" aria-label="edit">
              <Link to={`/roles/${product}/${role.id}/edit`}>
                <Pencil className="size-4" />
              </Link>
            </Button>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button size="icon" variant="ghost" aria-label="delete">
                  <Trash2 className="size-4" />
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>{t('role.list.delete')}</AlertDialogTitle>
                  <AlertDialogDescription>{t('role.list.deleteDescription', { role: label })}</AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>{tCommon('ui.button.cancel')}</AlertDialogCancel>
                  <AlertDialogAction onClick={() => remove(role.id)}>{tCommon('ui.button.confirm')}</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </>
        )}
      </div>
    </div>
  )
}
